import {
  CardPayment,
  CardPaymentRequest,
  CardRequest,
  DirectDebitDetails,
  OrderInquiryResponse,
  PaymentAssistBeginResponse,
  PaymentAssistRequest,
  PaymentAssistResponse,
  PaymentSuccessResponse,
  Result
} from '../components/common/CommonTypes';
import {
  BankResponse,
  MerchantSessionKey
} from '../components/common/SinglePayment/Opayo';
import { ApiHelper } from './apiHelper';
import { SagePayAccount } from '../consts/Common';

/**
 * Client-side API wrapper for payment-related endpoints.
 */
export class PaymentApi {
  /**
   * Validates Direct Debit details.
   *
   * @param directDebitDetails Direct debit details to validate.
   * @param token Bearer token used for authenticated requests.
   * @param callback Invoked with validation result on success.
   * @param failureCallback Invoked with an error message on failure.
   * @param signal Abort signal used to cancel the request.
   */
  validateDirectDebit(
    directDebitDetails: DirectDebitDetails,
    token: string,
    callback: (data: Result) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/directdebit';

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      directDebitDetails,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves the merchant session key required for card payments.
   *
   * @param token Bearer token used for authenticated requests.
   * @param account SagePay account selector (e.g. call centre vs default).
   * @param countryCode Two-letter ISO country code.
   * @param callback Invoked with the merchant session key on success.
   * @param failureCallback Invoked when the request fails.
   * @param signal Abort signal used to cancel the request.
   */
  getMerchantSessionKey(
    token: string,
    account: SagePayAccount,
    countryCode: string,
    callback: (data: MerchantSessionKey) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/merchantkey${account}?countryCode=${countryCode}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Submits a card payment.
   *
   * @param cardIdentifier Card identifier (obtained from the provider SDK).
   * @param merchantSessionKey Merchant session key (obtained from the backend).
   * @param payment Payment details.
   * @param token Bearer token used for authenticated requests.
   * @param account SagePay account selector (e.g. call centre vs default).
   * @param countryCode Two-letter ISO country code.
   * @param callback Invoked with payment result data on success.
   * @param failureCallback Invoked with an error message on failure.
   * @param signal Abort signal used to cancel the request.
   */
  submitPayment(
    cardIdentifier: string,
    merchantSessionKey: string,
    payment: CardPayment,
    token: string,
    account: SagePayAccount,
    countryCode: string,
    callback: (data: PaymentSuccessResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment${account}`;

    const browserLanguage = navigator.language;
    const browserJavaEnabled = navigator.javaEnabled();
    const browserColorDepth = screen.colorDepth;
    const browserScreenHeight = screen.height;
    const browserScreenWidth = screen.width;

    const d = new Date();
    const browserTZ = d.getTimezoneOffset();

    const cardRequest: CardRequest = {
      cardIdentifier: cardIdentifier,
      merchantSessionKey: merchantSessionKey,
      cardPayment: payment,
      browserLanguage: browserLanguage,
      browserJavaEnabled: browserJavaEnabled,
      browserColorDepth: browserColorDepth.toString(),
      browserScreenHeight: browserScreenHeight.toString(),
      browserScreenWidth: browserScreenWidth.toString(),
      browserTZ: browserTZ.toString()
    };

    const data: CardPaymentRequest = {
      cardRequest: cardRequest,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Requests a void on a previously submitted payment transaction.
   *
   * @param transactionId Payment transaction identifier.
   * @param countryCode Two-letter ISO country code.
   * @param token Bearer token used for authenticated requests.
   * @param account SagePay account selector (e.g. call centre vs default).
   * @param callback Invoked on success.
   * @param failureCallback Invoked on failure.
   * @param signal Abort signal used to cancel the request.
   */
  voidPayment(
    transactionId: string,
    countryCode: string,
    token: string,
    account: SagePayAccount,
    callback: () => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/void${account}`;

    const data = {
      transactionId: transactionId,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves the bank response/payment state for a transaction.
   *
   * @param transactionId Payment transaction identifier.
   * @param token Bearer token used for authenticated requests.
   * @param callback Invoked with bank response data on success.
   * @param failureCallback Invoked on failure.
   * @param signal Abort signal used to cancel the request.
   */
  getBankResponse(
    transactionId: string,
    token: string,
    callback: (data: BankResponse) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/bankresponse/${transactionId}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Sends an order using the WorldPay integration.
   *
   * @param token Bearer token used for authenticated requests.
   * @param payment Payment details.
   * @param isCallCentreUser Indicates call centre flow when true.
   * @param callback Invoked with payment result data on success.
   * @param failureCallback Invoked on failure.
   * @param countryCode Two-letter ISO country code.
   * @param language Browser language/culture to send.
   * @param signal Abort signal used to cancel the request.
   */
  sendOrder(
    token: string,
    payment: CardPayment,
    isCallCentreUser: boolean,
    callback: (data: PaymentSuccessResponse) => void,
    failureCallback: () => void,
    countryCode: string,
    language: string,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/wp${isCallCentreUser ? '/cc' : ''}`;

    const browserJavaEnabled = navigator.javaEnabled();
    const browserColorDepth = screen.colorDepth;
    const browserScreenHeight = screen.height;
    const browserScreenWidth = screen.width;

    const d = new Date();
    const browserTZ = d.getTimezoneOffset();

    const cardRequest: CardRequest = {
      cardIdentifier: '',
      merchantSessionKey: '',
      cardPayment: payment,
      browserLanguage: language,
      browserJavaEnabled: browserJavaEnabled,
      browserColorDepth: browserColorDepth.toString(),
      browserScreenHeight: browserScreenHeight.toString(),
      browserScreenWidth: browserScreenWidth.toString(),
      browserTZ: browserTZ.toString()
    };

    const data: CardPaymentRequest = {
      cardRequest: cardRequest,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Posts a WorldPay payment response back to the backend.
   *
   * @param token Bearer token used for authenticated requests.
   * @param orderNo WorldPay order number.
   * @param status Payment status.
   * @param error Error message/status from the provider.
   * @param customerEmail Customer email address stored against the WorldPay transaction.
   * @param signal Abort signal used to cancel the request.
   */
  paymentResponse(
    token: string,
    orderNo: string,
    status: string,
    error: string,
    customerEmail: string,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/wp/response`;

    const data = {
      orderNo: orderNo,
      status: status,
      error: error,
      customerEmail: customerEmail
    };

    new ApiHelper().postDataSecureAsync(dataUrl, data, token, signal);
  }

  /**
   * Updates a WorldPay payment (e.g. void/refund) for an existing order.
   */
  updateWorldPayPayment(
    token: string,
    orderNo: string,
    updateType: string,
    isCallCentreUser: boolean,
    amount: number,
    policyNumbers: string,
    customerName: string,
    customerEmail: string,
    callback: () => void,
    failureCallback: () => void,
    countryCode: string,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/singlepayment/wp';

    const data = {
      orderNo: orderNo,
      updateType: updateType,
      isCallCentreUser: isCallCentreUser,
      countryCode: countryCode,
      amount: amount,
      policyNumbers: policyNumbers,
      customerName: customerName,
      customerEmail: customerEmail
    };

    new ApiHelper().putDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  /**
   * Initiates a Payment Assist request.
   */
  beginPaymentAssistRequest(
    paymentAssistRequest: PaymentAssistRequest,
    token: string,
    successCallback: (data: PaymentAssistBeginResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/paymentassist';

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      paymentAssistRequest,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves the current Payment Assist transaction state for a given token.
   */
  getPaymentAssistState(
    id: string,
    successCallback: (response: PaymentAssistResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/paymentassist/transactions/${id}`;

    new ApiHelper().getDataAsync(
      dataUrl,
      successCallback,
      failureCallback,
      signal
    );
  }

  /**
   * Retrieves order/inquiry details for a WorldPay order.
   */
  getOrderInquiry(
    orderNo: string,
    isCallCentreUser: boolean,
    brandName: string,
    countryCode: string,
    token: string,
    inquirySuccessCallback: (response: OrderInquiryResponse) => void,
    inquiryFailureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/wp/orderinquiry?orderNo=${orderNo}&isCallCentreUser=${isCallCentreUser}&brandName=${brandName}&countryCode=${countryCode}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      inquirySuccessCallback,
      inquiryFailureCallback,
      signal
    );
  }
}
